import { Logger } from '@saviynt/common';
import * as Yup from 'yup';

import { VALIDATION_TYPES } from '../../../../../shared/constants/global';
import { NUMERIC_VALUE_VALIDATION } from '../../../../../shared/constants/validation';
import {
  validateNumberExpression,
  validateNumberRegex,
} from '../expressionHelpers';
import {
  type FieldSchema,
  getFieldValidations,
  type ValidatorContext,
} from '../types';

/**
 * Parses a regex string that may be in "/pattern/flags" format or plain pattern format.
 * @param regexString - The regex string to parse (e.g., "/^https?:\\/\\/\\S*$/i" or "^https?:\\/\\/\\S*$")
 * @returns A RegExp object
 */
const parseRegexString = (regexString: string): RegExp => {
  const trimmed = regexString.trim();
  const match = trimmed.match(/^\/(.+)\/([a-z]*)$/);

  if (match) {
    // Format: "/pattern/flags"
    const [, pattern, flags] = match;

    return new RegExp(pattern, flags);
  }

  // Plain pattern without slashes
  return new RegExp(trimmed);
};

const createNumberValidator = (context: ValidatorContext): FieldSchema => {
  const {
    field,
    l,
    isRequired,
    requiredMessage,
    currentValues,
    touched,
    entityData,
  } = context;
  const validations = getFieldValidations(field);

  let validator = Yup.number()
    .transform((value: number, originalValue: string | null) => {
      if (originalValue === '' || originalValue === null) {
        return undefined;
      }

      return value as number | undefined;
    })
    .nullable()
    .test(
      'max-chars',
      l(NUMERIC_VALUE_VALIDATION.ERROR_MESSAGES.MAX_CHARS_EXCEEDED),
      (_, ctx) => {
        const raw = ctx.originalValue as string | null | undefined;

        if (raw == null || raw === '') {
          return true;
        }

        return String(raw).length <= NUMERIC_VALUE_VALIDATION.MAX_CHARS;
      }
    );

  if (isRequired && 'isReadOnly' in field && !field.isReadOnly) {
    validator = validator.required(
      requiredMessage ?? l('This field is required')
    );
  }

  validations.forEach((rule) => {
    switch (rule.type) {
      case VALIDATION_TYPES.MIN_VALUE: {
        const minVal = Number(rule.value);

        if (!Number.isNaN(minVal)) {
          validator = validator.min(
            minVal,
            rule.message || l('Minimum value is {value}', { value: minVal })
          );
        }

        break;
      }

      case VALIDATION_TYPES.MAX_VALUE: {
        const maxVal = Number(rule.value);

        if (!Number.isNaN(maxVal)) {
          validator = validator.max(
            maxVal,
            rule.message || l('Maximum value is {value}', { value: maxVal })
          );
        }

        break;
      }

      case VALIDATION_TYPES.REGEX: {
        if (typeof rule.value === 'string') {
          try {
            const pattern = String(rule.value).trim();

            if (pattern.length > 0 && pattern.length <= 256) {
              const regexPattern = parseRegexString(pattern);

              validator = validator.test(
                'regex-validation',
                rule.message || l('Invalid format'),
                (value) => validateNumberRegex(value, regexPattern)
              );
            }
          } catch (error) {
            Logger.warn('Invalid regex pattern for number field:', error);
          }
        }

        break;
      }

      case VALIDATION_TYPES.EXPRESSION: {
        if (rule.value && typeof rule.value === 'string') {
          const ruleValue = rule.value;

          validator = validator.test(
            'expression-validation',
            rule.message || l('Validation failed'),
            (value) =>
              validateNumberExpression(value, ruleValue, {
                values: currentValues ?? {},
                touched,
                entityData: entityData ?? null,
              })
          );
        }

        break;
      }

      default:
        break;
    }
  });

  return validator;
};

export default createNumberValidator;
